import React from "react";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";

import Toolbar from "@mui/material/Toolbar";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import { KMKDialog } from "../../../../../Common";

const examples = [
  {
    fileName: "100 Sales Records.csv",
    regexPattern: "^(\\d+) (Sales).*\\.csv$",
    replacePattern: "Top $1 $2 Data 2024.csv",
    result: "Top 100 Sales Data 2024.csv",
    explanation:
      "Given RGEX matches the filename '100 Sales Records.csv'. The pattern works as follows: `^(\\d+)` captures the leading numeric part '100'; `(Sales)` captures the word 'Sales'; `.*` matches any characters following 'Sales', which is ' Records'; and `\\.csv$` ensures the filename ends with '.csv'. In the replacement pattern `Top $1 $2 Data 2024.csv`, `$1` and `$2` are replaced with the captured numeric value and the word 'Sales', respectively. This results in the filename being reformatted to 'Top 100 Sales Data 2024.csv'.",
  },
  {
    fileName: "report_2024_v1.txt",
    regexPattern: "^report_(\\d{4})_v(\\d+)",
    replacePattern: "annual_report_$1_v$2",
    result: "annual_report_2024_v1.txt",
    explanation:
      "Given RGEX the filename 'report_2024_v1.txt'. The `\\d{4}` captures the 4-digit year '2024', and `\\d+` captures the version '1'. In the replacement pattern `annual_report_$1_v$2`, `$1` and `$2` refer to the captured year and version, respectively. The filename is reformatted to 'annual_report_2024_v1.txt'.",
  },
  {
    fileName: "data_backup_20230920.zip",
    regexPattern: "data_backup_(\\d{8})\\.zip",
    replacePattern: "backup_$1.zip",
    result: "backup_20230920.zip",
    explanation:
      "Given RGEX matches the filename 'data_backup_20230920.zip'. The `\\d{8}` captures the 8-digit date '20230920'. In the replacement pattern `backup_$1.zip`, `$1` refers to the captured date. The filename is reformatted by removing 'data_' and retaining the date, resulting in 'backup_20230920.zip'.",
  },
  {
    fileName: "report.txt",
    regexPattern: "^(\\d+).*\\.csv$",
    replacePattern: "$1_$date{MM-DD-YYYY}.csv",
    result: "report_12-26-2012.csv",
    explanation:
      "Given RGEX matches the filename 'report.txt'. The `(\\d+)` captures everything before .csv. In the replacement pattern `$1` refers to filename without extension and $date{MM-DD-YYYY} will be replaced with data in specified format.",
  },
];

const supportedDateFormats = [
  "$date",
  "$datetime",
  "$date{DD-MM-YYYY}",
  "$date{DD-MM-YY}",
  "$date{YY-MM-DD}",
  "$date{MM-DD-YYYY}",
  "$date{MM-DD-YY}",
  "$date{YYYY-MM-DD}",
  "$date{MMDDYY}",
  "$date{DDMMYY}",
  "$date{YYMMDD}",
  "$datetime{HH:mm:ss}",
  "$datetime{DD-MM-YYYY HH:mm:ss}",
];

const DateFormatsLi = supportedDateFormats.map((format) => (
  <li key={format}>
    {format} <br />
  </li>
));

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiDialogActions-root": {
      padding: "0 20px 10px 0",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "400px",
    maxHeight: "600px",
    overflow: "auto",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  container: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  table: {
    width: "100%",
  },
  tableHead: {
    backgroundColor: "#f2f2f2",
  },
  tableCell: {
    textAlign: "left",
  },
}));

const RegexHelp = ({ onClose }) => {
  const { classes } = useStyles();

  return (
    <KMKDialog className={classes.dialogPaper} open={true} maxWidth="md" onClose={onClose}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>Rename files</Toolbar>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box
        sx={{
          width: "100%",
        }}
        className={classes.container}
      >
        <Box component="form" className={classes.formContainer}>
          <DialogContent style={{ gap: "20px" }}>
            <Container>
              <Typography variant="body1" gutterBottom component="div">
                This helps you understand how to use regex patterns for renaming files. You can provide a regex pattern
                and a replacement pattern, and the system will rename files accordingly when they are copied from one
                source to another.Regular expressions are patterns used to match character combinations in strings.
              </Typography>

              <Typography variant="body1" component="div" mt={2}>
                <strong>Regex Match Pattern Syntax:</strong>
              </Typography>
              <List>
                <ListItem>
                  <Typography variant="body1">
                    <strong>Dot (.)</strong>: Matches any single character except newline.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>Asterisk (*)</strong>: Matches zero or more of the preceding character.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>Square Brackets ([ ])</strong>: Matches any one of the enclosed characters.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>Parentheses (())</strong>: Groups patterns.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>Backslash (\)</strong>: Escapes special characters.
                  </Typography>
                </ListItem>
              </List>

              <Typography variant="body1" gutterBottom component="div" mt={2}>
                <strong>Replace Pattern Syntax:</strong>
              </Typography>
              <List>
                <ListItem>
                  <Typography variant="body1">
                    <strong>$1, $2, $3, ...</strong>: Refers to the text matched by the first, second, third, etc.,
                    capturing group in the regex pattern. For example, <code>$1</code> refers to the text matched by the
                    first pair of parentheses.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>$&</strong>: Inserts the entire matched text.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>$`</strong>: Inserts the portion of the text before the match.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>$'</strong>: Inserts the portion of the text after the match.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography variant="body1">
                    <strong>$date and $datetime</strong>: Insert the date or date & time to the renamed files.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                  <Box>
                    Currently supports: <br />
                  </Box>

                  <ul>{DateFormatsLi}</ul>
                </ListItem>
              </List>

              <Typography variant="body1" gutterBottom component="div" mt={2}>
                Use these patterns to rename files based on your needs. Ensure that your regex patterns correctly match
                the filenames you intend to rename.
              </Typography>

              <Typography variant="body1" gutterBottom component="div" mt={2}>
                <strong>Examples:</strong>
              </Typography>
              <TableContainer component={Paper}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCell}>
                        <strong>File Name</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <strong>Regex Pattern</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <strong>Replace Pattern</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <strong>Result</strong>
                      </TableCell>
                      <TableCell className={classes.tableCell} sx={{ minWidth: "600px" }}>
                        <strong>Explanation</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {examples.map((example, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.tableCell}>
                          <code className={classes.code}>{example.fileName}</code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <code className={classes.code}>{example.regexPattern}</code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <code className={classes.code}>{example.replacePattern}</code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <code className={classes.code}>{example.result}</code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>{example.explanation}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </DialogContent>
        </Box>
      </Box>
    </KMKDialog>
  );
};

export default RegexHelp;
